import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { formatDate } from '../../utils/dateFormatter';
import './AdminUsers.css';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    });

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    
    const initialFormState = {
        name: '',
        email: '',
        password: '',
        mobile: '',
        role: 'user',
        business_name: '',
        address: ''
    };
    
    const [form, setForm] = useState(initialFormState);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchUsers();
    }, [pagination.page, searchQuery]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit
            });
            // Add search parameter if search query exists
            if (searchQuery.trim()) {
                params.append('search', searchQuery.trim());
            }
            const response = await api.get(`/admin/users?${params}`);
            if (response.data.success) {
                setUsers(response.data.data.users);
                setPagination(response.data.data.pagination);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e) => {
        let { name, value } = e.target;
        if (name === 'mobile') {
            value = value.replace(/\D/g, '').slice(0, 10);
        }
        setForm({ ...form, [name]: value });
        setError('');
    };

    const resetForm = () => {
        setForm(initialFormState);
        setError('');
        setSuccess('');
        setEditingUser(null);
    };

    const openAddModal = () => {
        resetForm();
        setShowAddModal(true);
    };

    const openEditModal = (user) => {
        setEditingUser(user);
        setForm({
            name: user.name || '',
            email: user.email || '',
            password: '', // Don't pre-fill password
            mobile: user.mobile || '',
            role: user.role || 'user',
            business_name: user.business_name || '',
            address: user.address || ''
        });
        setError('');
        setSuccess('');
        setShowEditModal(true);
    };

    const closeModals = () => {
        setShowAddModal(false);
        setShowEditModal(false);
        resetForm();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Front-end validations
        if (form.mobile && !/^[0-9]{10}$/.test(form.mobile)) {
            setError('Mobile number must be exactly 10 digits');
            return;
        }

        if (['franchise', 'franchise_tags'].includes(form.role) && !form.address) {
            setError('Address is mandatory for franchise roles');
            return;
        }

        setSubmitting(true);
        setError('');
        setSuccess('');

        try {
            const response = await api.post('/admin/users', form);
            if (response.data.success) {
                setSuccess('User created successfully!');
                fetchUsers();
                setTimeout(() => closeModals(), 1500);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create user');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();

        if (!editingUser) return;

        // Front-end validations
        if (form.mobile && !/^[0-9]{10}$/.test(form.mobile)) {
            setError('Mobile number must be exactly 10 digits');
            return;
        }

        if (['franchise', 'franchise_tags'].includes(form.role) && !form.address) {
            setError('Address is mandatory for franchise roles');
            return;
        }

        setSubmitting(true);
        setError('');
        setSuccess('');

        try {
            // Build update payload - only include fields that have values
            const updateData = {};
            if (form.name) updateData.name = form.name;
            if (form.email) updateData.email = form.email;
            if (form.password) updateData.password = form.password; // Only send if changed
            if (form.mobile) updateData.mobile = form.mobile;
            if (form.role) updateData.role = form.role;
            if (form.business_name !== undefined) updateData.business_name = form.business_name;
            if (form.address !== undefined) updateData.address = form.address;

            const response = await api.put(`/admin/users/${editingUser.id}`, updateData);
            if (response.data.success) {
                setSuccess('User updated successfully!');
                fetchUsers();
                setTimeout(() => closeModals(), 1500);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update user');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            await api.delete(`/admin/users/${id}`);
            fetchUsers();
        } catch (err) {
            alert('Failed to delete user');
        }
    };

    const renderUserForm = (isEdit = false) => (
        <form onSubmit={isEdit ? handleEditSubmit : handleSubmit}>
            {error && <div className="alert error">{error}</div>}
            {success && <div className="alert success">{success}</div>}

            <div className="form-group">
                <label>Full Name</label>
                <input type="text" name="name" value={form.name} onChange={handleFormChange} required={!isEdit} />
            </div>
            <div className="form-group">
                <label>Email Address</label>
                <input type="email" name="email" value={form.email} onChange={handleFormChange} required={!isEdit} />
            </div>
            <div className="form-group">
                <label>{isEdit ? 'New Password (leave blank to keep current)' : 'Password'}</label>
                <input 
                    type="password" 
                    name="password" 
                    value={form.password} 
                    onChange={handleFormChange} 
                    required={!isEdit}
                    placeholder={isEdit ? 'Leave blank to keep current password' : ''}
                />
            </div>
            <div className="form-group">
                <label>Role</label>
                <select name="role" value={form.role} onChange={handleFormChange}>
                    <option value="user">User</option>
                    <option value="franchise">Restaurant Franchise</option>
                    <option value="franchise_tags">Franchise Tags</option>
                    <option value="admin">Admin</option>
                </select>
            </div>

            {['franchise', 'franchise_tags'].includes(form.role) && (
                <>
                    <div className="form-group">
                        <label>Business Name</label>
                        <input type="text" name="business_name" value={form.business_name} onChange={handleFormChange} placeholder="Restaurant or Agency Name" />
                    </div>
                    <div className="form-group">
                        <label>Full Address *</label>
                        <textarea
                            name="address"
                            value={form.address}
                            onChange={handleFormChange}
                            required
                            placeholder="Mandatory for franchises"
                            style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                        />
                    </div>
                </>
            )}

            <div className="form-group">
                <label>Mobile Number (10 digits)</label>
                <div className="mobile-input-wrapper-admin" style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-input)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', overflow: 'hidden' }}>
                    <span style={{ padding: '0.75rem', background: 'rgba(255,255,255,0.05)', borderRight: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-secondary)', fontWeight: 600 }}>+91</span>
                    <input
                        type="tel"
                        name="mobile"
                        value={form.mobile}
                        onChange={handleFormChange}
                        maxLength="10"
                        placeholder="9876543210"
                        style={{ border: 'none', flex: 1, padding: '0.75rem', outline: 'none' }}
                    />
                </div>
            </div>
            <button type="submit" className="btn-submit" disabled={submitting}>
                {submitting ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update User' : 'Create User')}
            </button>
        </form>
    );

    return (
        <div className="admin-users">
            <header className="page-header">
                <div className="header-info">
                    <h2>User Management</h2>
                    <p>View and manage all registered users</p>
                </div>
                <button className="btn-primary" onClick={openAddModal}>
                    <span>➕</span> Add User
                </button>
            </header>

            <div className="card">
                {/* Search Bar */}
                <div style={{ 
                    padding: '1rem', 
                    borderBottom: '1px solid var(--border-color)',
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                            Search by Name or Contact Number
                        </label>
                        <input
                            type="text"
                            placeholder="Enter name or contact number..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                setPagination({ ...pagination, page: 1 });
                            }}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                borderRadius: '0.5rem',
                                border: '1px solid var(--border-color)',
                                background: 'var(--bg-input)',
                                color: 'var(--text-primary)'
                            }}
                        />
                    </div>
                    {searchQuery && (
                        <button
                            onClick={() => {
                                setSearchQuery('');
                                setPagination({ ...pagination, page: 1 });
                            }}
                            style={{
                                padding: '0.75rem 1.5rem',
                                borderRadius: '0.5rem',
                                border: '1px solid var(--border-color)',
                                background: 'var(--bg-card)',
                                color: 'var(--text-primary)',
                                cursor: 'pointer',
                                marginTop: '1.75rem'
                            }}
                        >
                            Clear
                        </button>
                    )}
                </div>
                <div className="table-container">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>User</th>
                                <th>Contact</th>
                                <th>Role</th>
                                <th>Joined</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" className="text-center">Loading users...</td></tr>
                            ) : users.length === 0 ? (
                                <tr><td colSpan="6" className="text-center">No users found</td></tr>
                            ) : (
                                users.map(user => (
                                    <tr key={user.id}>
                                        <td>#{user.id}</td>
                                        <td>
                                            <div className="user-cell">
                                                <div className="user-avatar">{user.name ? user.name.charAt(0).toUpperCase() : (user.mobile ? user.mobile.charAt(0) : '?')}</div>
                                                <div className="user-info">
                                                    <span className="user-name">{user.name || 'No Name'}</span>
                                                    <span className="user-email">{user.email || user.mobile || '-'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{user.mobile || '-'}</td>
                                        <td>
                                            <span className={`badge ${user.role}`}>
                                                {user.role}
                                            </span>
                                        </td>
                                        <td>{formatDate(user.created_at)}</td>
                                        <td>
                                            <div className="action-btns">
                                                <button className="btn-icon-only edit" onClick={() => openEditModal(user)}>✏️</button>
                                                <button className="btn-icon-only delete" onClick={() => handleDelete(user.id)}>🗑️</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="pagination">
                    <button
                        disabled={pagination.page === 1}
                        onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    >
                        Previous
                    </button>
                    <span>Page {pagination.page} of {pagination.pages}</span>
                    <button
                        disabled={pagination.page === pagination.pages}
                        onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    >
                        Next
                    </button>
                </div>
            </div>

            {/* Add User Modal */}
            {showAddModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <header className="modal-header">
                            <h3>Add New User</h3>
                            <button className="close-btn" onClick={closeModals}>×</button>
                        </header>
                        {renderUserForm(false)}
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {showEditModal && editingUser && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <header className="modal-header">
                            <h3>Edit User: {editingUser.name || editingUser.mobile || `#${editingUser.id}`}</h3>
                            <button className="close-btn" onClick={closeModals}>×</button>
                        </header>
                        {renderUserForm(true)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUsers;
